import React from "react";
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";
import { StyledColumn, StyledVirtualItem } from "./styled";

/**
 * VirtualizedColumn renders a single virtualized column of items.
 * Used internally by VirtualizedList for both single-column and multi-column layouts.
 * 
 * @param {Object} props
 * @param {Array<{item: any, originalIndex: number}>} props.data - Items to render in this column
 * @param {Function} props.renderItem - Render function: (item, originalIndex) => ReactNode
 * @param {React.RefObject} props.scrollContainerRef - Ref to the scrollable container (null for window scroll)
 * @param {boolean} props.useWindowScroll - Use window/page scroll instead of container scroll
 * @param {number} props.estimateSize - Estimated height of each item
 * @param {number} props.overscan - Number of items to render beyond visible area
 * @param {number} props.gap - Gap between items in pixels
 * @param {Function} props.getItemKey - Optional custom key generator: (item, index) => string
 * @param {Function} props.onVisibleRangeChange - Optional callback when visible range changes
 */
export const VirtualizedColumn = ({
  data,
  renderItem,
  scrollContainerRef,
  useWindowScroll = false,
  estimateSize = 350,
  overscan = 3,
  gap = 16,
  getItemKey,
  onVisibleRangeChange,
}) => {
  // Common virtualizer options
  const commonOptions = {
    count: data.length,
    estimateSize: () => estimateSize,
    overscan,
    gap, // Add gap between items
    getItemKey: getItemKey
      ? (index) => getItemKey(data[index].item, data[index].originalIndex)
      : (index) => data[index]?.originalIndex ?? index,
    onChange: onVisibleRangeChange
      ? (instance) => {
          const range = instance.range;
          if (range) {
            onVisibleRangeChange({
              startIndex: range.startIndex,
              endIndex: range.endIndex,
            });
          }
        }
      : undefined,
  };

  // Use window virtualizer for page scroll, regular virtualizer for container scroll
  const windowVirtualizer = useWindowVirtualizer({
    ...commonOptions,
    enabled: useWindowScroll,
  });

  const containerVirtualizer = useVirtualizer({
    ...commonOptions,
    getScrollElement: () => scrollContainerRef?.current ?? null,
    enabled: !useWindowScroll,
  });

  const virtualizer = useWindowScroll ? windowVirtualizer : containerVirtualizer;
  const virtualItems = virtualizer.getVirtualItems();

  return (
    <StyledColumn $height={virtualizer.getTotalSize()}>
      {virtualItems.map((virtualItem) => {
        const { item, originalIndex } = data[virtualItem.index];
        
        return (
          <StyledVirtualItem
            key={virtualItem.key}
            data-index={virtualItem.index}
            ref={virtualizer.measureElement}
            $translateY={virtualItem.start}
          >
            {renderItem(item, originalIndex)}
          </StyledVirtualItem>
        );
      })}
    </StyledColumn>
  );
};
